import { useTranslation } from "react-i18next";
import LanguageSwitcher from "../../shared/components/LanguageSwitcher";
import { useState } from "react";
import ThemeToggle from "./ThemeToggle";
import PwaInstallButton from "../../shared/components/PwaInstallButton";

function getCurrentPerformer() {
  try {
    const rawPerformer = window.sessionStorage.getItem(
      "paranoidi_auth_performer"
    );

    return rawPerformer ? JSON.parse(rawPerformer) : {};
  } catch {
    return {};
  }
}

function getCurrentAdminPublicPath() {
  const performer = getCurrentPerformer();
  const slug = String(performer?.slug || "").trim();

  return slug ? `/p/${slug}` : "/p/demo";
}

function getCurrentPathname() {
  if (typeof window === "undefined") {
    return "/admin";
  }

  return window.location.pathname.replace(/\/+$/, "") || "/";
}

function getNavbarApiBaseUrl() {
  const fromWindow =
    typeof window !== "undefined"
      ? window.PARANOIDI_API_BASE_URL
      : "";

  if (fromWindow) {
    return String(fromWindow).replace(/\/+$/, "");
  }

  if (
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1")
  ) {
    return "http://localhost:4000/api";
  }

  return "/backend/api";
}

const PARANOIDI_NAVBAR_LOGO_URL = "/brand/paranoidi-rockhand-192.png";

function getDesktopNavLinkClass(isActive) {
  return [
    "paranoidi-rock-nav-link relative overflow-hidden rounded-full px-3 py-2 text-xs font-black uppercase tracking-[0.12em] no-underline transition duration-300 sm:px-4",
    isActive
      ? "is-active bg-gradient-to-r from-red-700 via-red-500 to-red-700 text-white shadow-[0_0_26px_rgba(239,68,68,0.42),inset_0_1px_0_rgba(255,255,255,0.22)] ring-1 ring-red-300/20"
          : [
          "text-zinc-300 hover:bg-white/10 hover:text-white",
          "hover:shadow-[0_0_20px_rgba(239,68,68,0.18)]",
          "theme-light:text-[#5a3824]",
          "theme-light:hover:bg-white/65 theme-light:hover:text-[#541717]",
          "theme-light:hover:shadow-[0_0_20px_rgba(84,23,23,0.12)]",
        ].join(" "),
  ].join(" ");
}

function getMobileNavLinkClass(isActive) {
  return [
    "group relative block overflow-hidden border-b border-white/10 px-5 py-4 text-sm font-black uppercase tracking-[0.16em] no-underline transition last:border-b-0 theme-light:border-[#5a3824]/12",
    "before:absolute before:left-0 before:top-1/2 before:h-7 before:w-1 before:-translate-y-1/2 before:rounded-r-full before:transition",
    isActive
      ? [
          "bg-red-500/[0.12] text-white before:bg-red-500",
          "shadow-[inset_0_0_28px_rgba(239,68,68,0.08)]",
          "theme-light:bg-[#ef4444]/10 theme-light:text-[#541717]",
          "theme-light:before:bg-[#ef4444]",
        ].join(" ")
      : [
          "text-zinc-300 before:bg-transparent",
          "hover:bg-white/[0.05] hover:text-white hover:before:bg-red-500/50",
          "theme-light:text-[#5a3824]",
          "theme-light:hover:bg-white/60 theme-light:hover:text-[#541717]",
          "theme-light:hover:before:bg-[#ef4444]/70",
        ].join(" "),
  ].join(" ");
}

export default function Navbar({ config }) {
  const { t, i18n } = useTranslation("common");
  const homePath = i18n.resolvedLanguage?.toLowerCase().startsWith("en")
    ? "/en/"
    : "/";

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const performer = getCurrentPerformer();
  const performerName =
    performer?.name || config?.artistName || config?.siteName || "Paranoidi";

  const currentPathname = getCurrentPathname();
  const publicPath = getCurrentAdminPublicPath();
  const isAnyMenuOpen = isUserMenuOpen || isMobileMenuOpen;

  function closeMenus() {
    setIsUserMenuOpen(false);
    setIsMobileMenuOpen(false);
  }

  function handleInternalNavigation(event, targetPath) {
    if (
      event?.metaKey ||
      event?.ctrlKey ||
      event?.shiftKey ||
      event?.altKey ||
      event?.button === 1
    ) {
      return;
    }

    event.preventDefault();

    const currentPath = window.location.pathname.replace(/\/+$/, "") || "/";
    const nextPath = String(targetPath || "/").replace(/\/+$/, "") || "/";

    closeMenus();

    if (currentPath === nextPath) {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
      return;
    }

    window.history.pushState({}, "", nextPath);

    window.dispatchEvent(new Event("paranoidi:navigate"));

    try {
      window.dispatchEvent(new PopStateEvent("popstate"));
    } catch {
      window.dispatchEvent(new Event("popstate"));
    }

    window.requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "auto" });
    });
  }

  async function handleLogoutClick() {
    closeMenus();

    window.sessionStorage.setItem(
      "paranoidi_transition_loader",
      "1"
    );

    try {
      const response = await fetch(
        `${getNavbarApiBaseUrl()}/auth/logout`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            Accept: "application/json",
          },
        }
      );

      if (!response.ok) {
        console.error(
          "Uloskirjautuminen palvelimelta epäonnistui:",
          response.status
        );
      }
    } catch (error) {
      console.error(
        "Uloskirjautumispyyntö epäonnistui:",
        error
      );
    }

    [
      "paranoidi_logged_in",
      "paranoidi_auth_performer",
      "paranoidi_auth_user",
      "paranoidi_performer_slug",
      "paranoidi_auth_type",
    ].forEach((key) => {
      window.sessionStorage.removeItem(key);
      window.localStorage.removeItem(key);
    });

    window.location.replace("/login");
  }

  return (
    <>
      <style>
        {`
          @keyframes paranoidiRockShimmer {
            0% {
              transform: translateX(-120%) skewX(-18deg);
              opacity: 0;
            }

            18% {
              opacity: 0.55;
            }

            48% {
              opacity: 0.18;
            }

            100% {
              transform: translateX(160%) skewX(-18deg);
              opacity: 0;
            }
          }

          @keyframes paranoidiGlitterDrift {
            0% {
              background-position: 0% 0%, 12% 18%, 80% 10%, 50% 100%;
              opacity: 0.22;
            }

            50% {
              background-position: 80% 30%, 28% 66%, 30% 40%, 90% 20%;
              opacity: 0.55;
            }

            100% {
              background-position: 0% 0%, 12% 18%, 80% 10%, 50% 100%;
              opacity: 0.22;
            }
          }

          @keyframes paranoidiMobileMenuSweep {
            0% {
              transform: translateX(-120%);
            }

            100% {
              transform: translateX(220%);
            }
          }

          @keyframes paranoidiMobileItemIn {
            0% {
              opacity: 0;
              transform: translateY(-10px);
            }

            100% {
              opacity: 1;
              transform: translateY(0);
            }
          }

          .paranoidi-rock-header::before {
            content: "";
            position: absolute;
            inset: 0;
            pointer-events: none;
            background:
              radial-gradient(circle at 8% 35%, rgba(255,255,255,0.24) 0 1px, transparent 2px),
              radial-gradient(circle at 28% 20%, rgba(248,113,113,0.35) 0 1px, transparent 2px),
              radial-gradient(circle at 72% 45%, rgba(255,255,255,0.18) 0 1px, transparent 2px),
              radial-gradient(circle at 90% 30%, rgba(250,204,21,0.18) 0 1px, transparent 2px);
            background-size: 140px 60px, 180px 70px, 220px 80px, 260px 90px;
            animation: paranoidiGlitterDrift 7s ease-in-out infinite;
            mix-blend-mode: screen;
          }

          .paranoidi-rock-header::after {
            content: "";
            position: absolute;
            left: -22%;
            top: 0;
            height: 100%;
            width: 28%;
            pointer-events: none;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255,255,255,0.10),
              rgba(248,113,113,0.20),
              transparent
            );
            animation: paranoidiRockShimmer 5.8s ease-in-out infinite;
          }

          .paranoidi-rock-nav-link::after {
            content: "";
            position: absolute;
            left: -55%;
            top: 0;
            height: 100%;
            width: 42%;
            pointer-events: none;
            background: linear-gradient(
              90deg,
              transparent,
              rgba(255,255,255,0.24),
              transparent
            );
            transform: skewX(-18deg);
            opacity: 0;
          }

          .paranoidi-rock-nav-link:hover::after,
          .paranoidi-rock-nav-link.is-active::after {
            animation: paranoidiRockShimmer 2.8s ease-in-out infinite;
          }

          .paranoidi-mobile-menu-sweep {
            animation: paranoidiMobileMenuSweep 3.2s ease-in-out infinite;
          }

          .paranoidi-mobile-menu-panel {
            max-height: 0;
            opacity: 0;
            transform: translateY(-10px);
            pointer-events: none;
            transition:
              max-height 260ms ease,
              opacity 220ms ease,
              transform 220ms ease;
          }

          .paranoidi-mobile-menu-panel.is-open {
            max-height: 39rem;
            opacity: 1;
            transform: translateY(0);
            pointer-events: auto;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item {
            animation: paranoidiMobileItemIn 240ms ease-out both;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--1 {
            animation-delay: 35ms;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--2 {
            animation-delay: 70ms;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--3 {
            animation-delay: 105ms;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--4 {
            animation-delay: 140ms;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--5 {
            animation-delay: 175ms;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--6 {
            animation-delay: 210ms;
          }

          .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--7 {
            animation-delay: 245ms;
          }

         .paranoidi-mobile-menu-panel.is-open .paranoidi-mobile-menu-item--8 {
            animation-delay: 280ms;
          }

          .paranoidi-nav-overlay {
            opacity: 0;
            pointer-events: none;
            transition: opacity 220ms ease;
          }

          .paranoidi-nav-overlay.is-open {
            opacity: 1;
            pointer-events: auto;
          }
        `}
      </style>

      <button
        type="button"
        aria-label={t("navigation.closeMenu")}
        aria-hidden={!isAnyMenuOpen}
        tabIndex={isAnyMenuOpen ? 0 : -1}
        onClick={closeMenus}
        className={[
          "paranoidi-nav-overlay fixed inset-0 z-[90]",
          "bg-black/92",
          isAnyMenuOpen ? "is-open" : "",
        ].join(" ")}
      />

      <header className="paranoidi-rock-header fixed left-0 right-0 top-0 z-[100] overflow-visible border-b border-[#ef4444]/30 bg-black/94 text-white shadow-[0_18px_55px_rgba(0,0,0,0.62),0_0_38px_rgba(127,29,29,0.16)] backdrop-blur-2xl transition-colors duration-300 theme-light:border-[#ef4444]/25 theme-light:bg-[#ead6c2]/95 theme-light:text-[#241a16] theme-light:shadow-[0_14px_40px_rgba(84,23,23,0.18),0_0_26px_rgba(239,68,68,0.10)]">
        <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(180deg,rgba(239,68,68,0.14),rgba(127,29,29,0.05)_42%,transparent_80%)] theme-light:bg-[linear-gradient(180deg,rgba(239,68,68,0.10),rgba(255,255,255,0.18)_42%,transparent_82%)]" />
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-red-400/70 to-transparent" />

        <div className="relative mx-auto flex max-w-7xl items-center justify-between gap-2 px-3 py-3 sm:gap-4 sm:px-5">
          <div className="relative flex min-w-0 items-center gap-3">
            <button
              type="button"
              onClick={() => {
                setIsUserMenuOpen((current) => !current);
                setIsMobileMenuOpen(false);
              }}
              className={[
                "flex h-9 w-9 shrink-0 items-center justify-center overflow-hidden rounded-lg",
                "border border-white/10 bg-white/[0.04]",
                "transition duration-200 hover:border-red-400/30 hover:bg-white/[0.08]",
              ].join(" ")}
              aria-label={t("navigation.openUserMenu")}
            >
              <img
                src={PARANOIDI_NAVBAR_LOGO_URL}
                alt="Paranoidi"
                className="h-7 w-7 object-contain"
              />
            </button>

            {isUserMenuOpen && (
              <div
                className={[
                  "fixed left-3 right-3 top-20 z-[130] overflow-hidden rounded-2xl",
                  "border border-red-400/20 bg-zinc-950 text-white",
                  "shadow-[0_28px_80px_rgba(0,0,0,0.72),0_0_34px_rgba(127,29,29,0.24)]",
                  "theme-light:border-[#5a3824]/20 theme-light:bg-[#fff8ef] theme-light:text-[#241a16]",
                  "theme-light:shadow-[0_24px_65px_rgba(84,23,23,0.22),0_0_28px_rgba(239,68,68,0.10)]",
                  "transition duration-200 ease-out",
                  "sm:absolute sm:left-0 sm:right-auto sm:top-14 sm:w-64",
                ].join(" ")}
              >
                <div className="relative overflow-hidden border-b border-white/10 px-4 py-4 theme-light:border-[#5a3824]/15">
                  <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(239,68,68,0.18),transparent_16rem)]" />

                  <p className="relative text-xs font-black uppercase tracking-[0.2em] text-red-400">
                    Paranoidi
                  </p>

                  <span className="relative hidden max-w-[12rem] truncate text-[0.68rem] font-black uppercase leading-tight tracking-[0.18em] text-white theme-light:text-[#241a16] xl:block">
                    {performerName}
                  </span>
                </div>

                <a
                  href="/admin/settings"
                  onClick={(event) =>
                    handleInternalNavigation(event, "/admin/settings")
                  }
                  className="block w-full border-b border-white/10 px-4 py-3 text-left text-sm font-black text-zinc-200 no-underline transition hover:bg-white/10 hover:text-white theme-light:border-[#5a3824]/15 theme-light:text-[#5a3824] theme-light:hover:bg-white/70 theme-light:hover:text-[#541717]"
                >
                  {t("navigation.settings")}
                </a>

                <a
                  href="/admin/subscription"
                  onClick={(event) =>
                    handleInternalNavigation(event, "/admin/subscription")
                  }
                  className="block w-full border-b border-white/10 px-4 py-3 text-left text-sm font-black text-zinc-200 no-underline transition hover:bg-white/10 hover:text-white theme-light:border-[#5a3824]/15 theme-light:text-[#5a3824] theme-light:hover:bg-white/70 theme-light:hover:text-[#541717]"
                >
                  {t("navigation.subscription")}
                </a>

                <PwaInstallButton
                  className="block w-full border-b border-white/10 px-4 py-3 text-left text-sm font-black text-zinc-200 transition hover:bg-white/10 hover:text-white theme-light:border-[#5a3824]/15 theme-light:text-[#5a3824] theme-light:hover:bg-white/70 theme-light:hover:text-[#541717]"
                >
                  {t("pwa.install")}
                </PwaInstallButton>

                <button
                  type="button"
                  onClick={handleLogoutClick}
                  className="block w-full px-4 py-3 text-left text-sm font-black text-red-300 transition hover:bg-red-500/10 hover:text-red-200 theme-light:text-red-700 theme-light:hover:bg-red-500/10 theme-light:hover:text-red-800"
                >
                  {t("navigation.logout")}
                </button>
              </div>
            )}

            <a
              href="/admin"
              onClick={(event) => handleInternalNavigation(event, "/admin")}
              className="min-w-0 no-underline"
            >
              <span className="block text-xs font-black uppercase leading-tight tracking-[0.22em] text-white theme-light:text-[#541717] sm:hidden">
                Paranoidi
              </span>

              <span className="block max-w-[13rem] truncate text-[0.62rem] font-black uppercase leading-tight tracking-[0.14em] text-white/90 theme-light:text-[#241a16]">
                {performerName}
              </span>

              <span className="block truncate text-[0.68rem] text-zinc-400 theme-light:text-[#8a5a35] sm:text-xs">
                {t("navigation.admin")}
              </span>
            </a>
          </div>

          <nav
            aria-label={t("navigation.adminMenu")}
            className="hidden min-w-0 shrink-0 items-center gap-1.5 overflow-x-auto rounded-full border border-red-400/20 bg-white/[0.045] p-1.5 shadow-[inset_0_0_24px_rgba(0,0,0,0.55),0_0_24px_rgba(127,29,29,0.12)] backdrop-blur-xl theme-light:border-[#5a3824]/18 theme-light:bg-[#fff8ef]/65 theme-light:shadow-[inset_0_0_18px_rgba(84,23,23,0.08),0_6px_22px_rgba(84,23,23,0.12)] xl:flex xl:gap-2"
          >
            <a
              href="/admin"
              onClick={(event) => handleInternalNavigation(event, "/admin")}
              className={getDesktopNavLinkClass(currentPathname === "/admin")}
            >
              {t("navigation.admin")}
            </a>

            <a
              href="/admin/faq"
              onClick={(event) => handleInternalNavigation(event, "/admin/faq")}
              className={getDesktopNavLinkClass(
                currentPathname === "/admin/faq"
              )}
            >
              {t("navigation.faq")}
            </a>

            <a
              href="/taydenna-tilaustasi"
              onClick={(event) =>
                handleInternalNavigation(event, "/taydenna-tilaustasi")
              }
              className={getDesktopNavLinkClass(
                currentPathname === "/taydenna-tilaustasi"
              )}
            >
              {t("navigation.printProducts")}
            </a>

            <a
              href="/yhteys"
              onClick={(event) =>
                handleInternalNavigation(event, "/yhteys")
              }
              className={getDesktopNavLinkClass(
                currentPathname === "/yhteys"
              )}
            >
              {t("navigation.contact")}
            </a>

            <a
              href={homePath}
              onClick={(event) =>
                handleInternalNavigation(event, homePath)
              }
              className={getDesktopNavLinkClass(false)}
            >
              {t("navigation.home")}
            </a>

            <a
              href={publicPath}
              onClick={(event) => handleInternalNavigation(event, publicPath)}
              className="paranoidi-rock-nav-link is-active relative inline-flex overflow-hidden rounded-full bg-gradient-to-r from-red-600 via-red-500 to-red-600 px-3 py-2 text-xs font-black uppercase tracking-[0.12em] text-white no-underline shadow-[0_0_28px_rgba(239,68,68,0.44),inset_0_1px_0_rgba(255,255,255,0.24)] ring-1 ring-red-200/20 transition duration-300 hover:scale-[1.02] hover:from-red-500 hover:via-red-400 hover:to-red-600 sm:px-5"
            >
              {t("navigation.myPage")}
            </a>

            <div className="flex shrink-0 items-center gap-2">
              <LanguageSwitcher variant="public" showLabel={false} />
              <ThemeToggle config={config} />
            </div>
          </nav>

          <div className="flex shrink-0 items-center gap-2 xl:hidden">
            <a
              href={publicPath}
              onClick={(event) => handleInternalNavigation(event, publicPath)}
              className="inline-flex rounded-full bg-gradient-to-r from-red-600 via-red-500 to-red-600 px-4 py-2.5 text-xs font-black uppercase tracking-[0.12em] text-white no-underline shadow-[0_0_24px_rgba(239,68,68,0.36)] transition hover:bg-red-400"
            >
              {t("navigation.myPage")}
            </a>

            <button
              type="button"
              onClick={() => {
                setIsMobileMenuOpen((current) => !current);
                setIsUserMenuOpen(false);
              }}
              className={[
                "inline-flex h-10 w-10 items-center justify-center rounded-full",
                "border border-red-400/20 bg-white/[0.04] text-white",
                "shadow-[inset_0_0_18px_rgba(0,0,0,0.45),0_0_18px_rgba(127,29,29,0.16)] transition hover:bg-white/10",
                "theme-light:border-[#5a3824]/20 theme-light:bg-[#fff8ef]/75 theme-light:text-[#541717]",
                "theme-light:shadow-[inset_0_0_12px_rgba(84,23,23,0.05),0_5px_18px_rgba(84,23,23,0.12)]",
                "theme-light:hover:bg-white",
              ].join(" ")}
              aria-label={t("navigation.openMobileMenu")}
              aria-expanded={isMobileMenuOpen}
            >
              <span className="grid gap-1">
                <span
                  className={[
                    "block h-0.5 w-4 rounded-full bg-white transition duration-200 theme-light:bg-[#541717]",
                    isMobileMenuOpen ? "translate-y-1.5 rotate-45" : "",
                  ].join(" ")}
                />
                <span
                  className={[
                    "block h-0.5 w-4 rounded-full bg-white transition duration-200 theme-light:bg-[#541717]",
                    isMobileMenuOpen ? "opacity-0" : "",
                  ].join(" ")}
                />
                <span
                  className={[
                    "block h-0.5 w-4 rounded-full bg-white transition duration-200 theme-light:bg-[#541717]",
                    isMobileMenuOpen ? "-translate-y-1.5 -rotate-45" : "",
                  ].join(" ")}
                />
              </span>
            </button>
          </div>
        </div>
      </header>

      <nav
        aria-label={t("navigation.mobileAdminMenu")}
        aria-hidden={!isMobileMenuOpen}
        className={[
          "paranoidi-mobile-menu-panel fixed left-0 right-0 top-[68px] z-[130] overflow-hidden",
          "border-b border-red-500/25 bg-[#050505]/98 text-white",
          "shadow-[0_32px_90px_rgba(0,0,0,0.9),0_0_44px_rgba(127,29,29,0.25)] backdrop-blur-2xl",
          "theme-light:border-[#ef4444]/25 theme-light:bg-[#ead6c2]/98 theme-light:text-[#241a16]",
          "theme-light:shadow-[0_28px_70px_rgba(84,23,23,0.25),0_0_30px_rgba(239,68,68,0.12)]",
          "xl:hidden",
          isMobileMenuOpen ? "is-open" : "",
        ].join(" ")}
      >
        <div
          className={[
            "relative flex items-center justify-between gap-4 overflow-hidden border-t border-red-500/20",
            "bg-[linear-gradient(90deg,rgba(239,68,68,0.22),rgba(255,255,255,0.04),rgba(239,68,68,0.12))]",
            "theme-light:bg-[linear-gradient(90deg,rgba(239,68,68,0.15),rgba(255,248,239,0.65),rgba(239,68,68,0.10))]",
            "px-5 py-3",
          ].join(" ")}
        >
          <div
            className={[
              "pointer-events-none absolute inset-y-0 left-0 w-1/2",
              "bg-[linear-gradient(90deg,transparent,rgba(248,113,113,0.38),rgba(255,255,255,0.14),transparent)]",
              "paranoidi-mobile-menu-sweep opacity-80",
            ].join(" ")}
          />

          <p className="relative text-[0.62rem] font-black uppercase tracking-[0.32em] text-red-300 theme-light:text-[#b42318]">
            {t("navigation.mobileMenuTitle")}
          </p>
          <div className="relative flex shrink-0 items-center gap-2">
            <LanguageSwitcher variant="public" showLabel={false} />
            <ThemeToggle config={config} />
          </div>
        </div>

        <div className="grid">
          <a
            href="/admin"
            onClick={(event) => handleInternalNavigation(event, "/admin")}
            tabIndex={isMobileMenuOpen ? 0 : -1}
            className={[
              getMobileNavLinkClass(currentPathname === "/admin"),
              "paranoidi-mobile-menu-item paranoidi-mobile-menu-item--1",
            ].join(" ")}
          >
            {t("navigation.admin")}
          </a>

          <a
            href="/admin/faq"
            onClick={(event) => handleInternalNavigation(event, "/admin/faq")}
            tabIndex={isMobileMenuOpen ? 0 : -1}
            className={[
              getMobileNavLinkClass(currentPathname === "/admin/faq"),
              "paranoidi-mobile-menu-item paranoidi-mobile-menu-item--4",
            ].join(" ")}
          >
            {t("navigation.faq")}
          </a>

          <a
            href="/taydenna-tilaustasi"
            onClick={(event) =>
              handleInternalNavigation(event, "/taydenna-tilaustasi")
            }
            tabIndex={isMobileMenuOpen ? 0 : -1}
            className={[
              getMobileNavLinkClass(
                currentPathname === "/taydenna-tilaustasi"
              ),
              "paranoidi-mobile-menu-item paranoidi-mobile-menu-item--5",
            ].join(" ")}
          >
            {t("navigation.printProducts")}
          </a>

          <a
            href="/yhteys"
            onClick={(event) =>
              handleInternalNavigation(event, "/yhteys")
            }
            tabIndex={isMobileMenuOpen ? 0 : -1}
            className={[
              getMobileNavLinkClass(currentPathname === "/yhteys"),
              "paranoidi-mobile-menu-item paranoidi-mobile-menu-item--7",
            ].join(" ")}
          >
            {t("navigation.contact")}
          </a>

          <a
            href={homePath}
            onClick={(event) => handleInternalNavigation(event, homePath)}
            tabIndex={isMobileMenuOpen ? 0 : -1}
            className={[
              getMobileNavLinkClass(false),
              "paranoidi-mobile-menu-item paranoidi-mobile-menu-item--6",
            ].join(" ")}
          >
            {t("navigation.home")}
          </a>
        </div>
      </nav>
    </>
  );
}